import { useEffect } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { useKeywordListComposerContext } from "./KeywordListComposerContext";
import { trackPopupClick } from "./tracking";
import { useKeywordListSuggestions } from "./useKeywordListSuggestions";
import { getSortedKeywords } from "./utils/getSortedKeywords";

export const KeywordListComposerSuggestionsBanner = () => {
    const t = useTranslation();
    const {
        suggestionsStatus,
        changeSuggestionsStatus,
        country,
        orderBy,
        seedKeyword,
        populateKeywords,
        deselectAllKeywords,
    } = useKeywordListComposerContext();

    const { status, data: keywords } = useKeywordListSuggestions({
        seedKeyword,
        countryId: country.id,
        enabled: suggestionsStatus === "pending",
        select: (data) => {
            // Prevents TanStack Query returning data from cache even if query is disabled
            if (suggestionsStatus === "idle") {
                return undefined;
            }
            return getSortedKeywords(
                data.keywords.map((keyword) => (keyword.term === seedKeyword ? { ...keyword, isSeed: true } : keyword)),
                orderBy,
            );
        },
    });

    useEffect(() => {
        if (suggestionsStatus !== "idle") {
            changeSuggestionsStatus(status);
        }
    }, [suggestionsStatus, status, changeSuggestionsStatus]);

    useEffect(() => {
        if (keywords && keywords?.length > 0) {
            populateKeywords(keywords);
        }
    }, [keywords, populateKeywords]);

    if (suggestionsStatus !== "idle" || seedKeyword === undefined) {
        return null;
    }

    return (
        <Flex
            position="absolute"
            bottom="0"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            padding="16px"
            background={colorsPalettes.sky[100]}
        >
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[500]}
            >
                {t("keyword_list_composer.suggestions_banner.text")}
            </Text>
            <IconButton
                type="flat"
                iconName="wand"
                onClick={() => {
                    changeSuggestionsStatus("pending");
                    deselectAllKeywords();
                    trackPopupClick({ event: "Load Suggestions", seedKeyword: seedKeyword });
                }}
            >
                {t("keyword_list_composer.suggestions_banner.button_label")}
            </IconButton>
        </Flex>
    );
};
